import type { ReactNode } from "react";
import { ssoIssuerUrlError } from "../../../../shared/ssoIssuerUrl";
import { Box } from "../../Box";
import { Input } from "../../Input";
import { Text } from "../../Text";
import { CallbackUrl } from "./CallbackUrl";
import { DEFAULT_OIDC_SCOPES, type SsoConfig } from "./constants";
import { Divider, DomainField, Field } from "./shared";

export function OidcFields({
  config,
  onConfigChange,
  existing,
  jitSection,
}: {
  config: SsoConfig;
  onConfigChange: (updater: (prev: SsoConfig) => SsoConfig) => void;
  existing?: boolean;
  jitSection: ReactNode;
}) {
  const isNew = !existing;
  const placeholder = isNew ? undefined : "Leave blank to keep current";
  const issuerError = config.issuer.trim()
    ? ssoIssuerUrlError(config.issuer.trim())
    : null;

  return (
    <>
      <Field>
        <Box horizontal={8} vertical={10}>
          <Text variant="text-small-md">Issuer URL</Text>
        </Box>
        <Input
          value={config.issuer}
          onChange={(e) =>
            onConfigChange((f) => ({ ...f, issuer: e.target.value }))
          }
          placeholder="https://your-org.okta.com"
          required={isNew}
          style={{ width: "100%" }}
        />
        {issuerError ? (
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-danger)">
              {issuerError}
            </Text>
          </Box>
        ) : (
          isNew && (
            <Box horizontal={8} top={6}>
              <Text variant="text-small" color="var(--text-secondary)">
                The issuer value from your IdP’s OIDC configuration
              </Text>
            </Box>
          )
        )}
      </Field>

      <DomainField
        value={config.domain}
        onChange={(domain) => onConfigChange((f) => ({ ...f, domain }))}
        isNew={isNew}
      />

      <Field>
        <Box horizontal={8} vertical={10}>
          <Text variant="text-small-md">Client ID</Text>
        </Box>
        <Input
          value={config.clientId}
          onChange={(e) =>
            onConfigChange((f) => ({ ...f, clientId: e.target.value }))
          }
          placeholder={isNew ? "OAuth client ID from your IdP" : placeholder}
          required={isNew}
          style={{ width: "100%" }}
        />
        {isNew && (
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              From your IdP’s OAuth application settings
            </Text>
          </Box>
        )}
      </Field>

      <Field>
        <Box horizontal={8} vertical={10}>
          <Text variant="text-small-md">Client secret</Text>
        </Box>
        <Input
          type="password"
          value={config.clientSecret}
          onChange={(e) =>
            onConfigChange((f) => ({
              ...f,
              clientSecret: e.target.value,
            }))
          }
          placeholder={
            isNew ? "OAuth client secret from your IdP" : placeholder
          }
          required={isNew}
          style={{ width: "100%" }}
        />
      </Field>

      <Field>
        <Box horizontal={8} vertical={10}>
          <Text variant="text-small-md">Scopes</Text>
        </Box>
        <Input
          value={config.scopes}
          onChange={(e) =>
            onConfigChange((f) => ({ ...f, scopes: e.target.value }))
          }
          placeholder={isNew ? DEFAULT_OIDC_SCOPES.join(" ") : placeholder}
          style={{ width: "100%" }}
        />
        {isNew && (
          <Box horizontal={8} top={6}>
            <Text variant="text-small" color="var(--text-secondary)">
              Comma- or space-separated scopes requested during sign-in. Leave
              blank to use the defaults: {DEFAULT_OIDC_SCOPES.join(", ")}.
            </Text>
          </Box>
        )}
      </Field>

      <Divider />

      {jitSection}

      <Divider />
      <CallbackUrl providerId={config.providerId} isNew={isNew} />
    </>
  );
}
